/**
 * Générateur — Identifier des situations de proportionnalité dans des contextes concrets
 * (prix, recettes, distances, échelles).
 * Attendu ID : 983 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 983.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Identifier des situations de proportionnalité dans des
 * contextes concrets (prix, recettes, distances, échelles). »
 *
 * L'objectif est l'IDENTIFICATION, jamais l'exploitation : `exclusions` 6 interdit tout calcul
 * demandé avant la décision. Aucune carte ne demande donc de calculer un prix, une longueur
 * réelle ou une vitesse — ces tâches relèvent des attendus voisins (coefficient, échelles).
 *
 * Catégorie « decider » (niveau 1 : quatre situations décrites en une phrase, une par contexte,
 * décision sans justification)
 *   - decider_tableau_vf     Tableau à deux colonnes « Proportionnelle / Non proportionnelle »
 *   - decider_qcm_unique     QCM : laquelle relève (ou ne relève pas) de la proportionnalité
 *   - decider_texte          Saisie : le numéro de la seule situation qui relève (ou non)
 *
 * Catégorie « raison » (niveau 2 : décision ET raison choisie parmi quatre)
 *   - raison_qcm_unique      QCM : décision + raison, deux raisons fautives courantes
 *   - raison_relier          Relier : chaque situation à la raison qui la décrit
 *   - raison_texte_trou_drag Trous (drag) : compléter la justification avec les mots justes
 *
 * Catégorie « valeurs » (niveau 3 : situations données par deux ou trois couples de valeurs)
 *   - valeurs_tableau_vf     Tableau : trois séries de couples, décider pour chacune
 *   - valeurs_qcm_unique     QCM : quatre séries, laquelle relève (ou ne relève pas)
 *   - valeurs_texte          Saisie : le numéro de la série qui ne relève pas (ou qui relève)
 *
 * Catégorie « voisines » (niveau 4 : deux situations très proches, une affirmation erronée à
 * corriger)
 *   - voisines_qcm_unique    QCM : la correction juste de l'affirmation proposée
 *   - voisines_tableau_vf    Tableau : quatre affirmations sur le couple de situations
 *   - voisines_texte_trou_drag Trous (drag) : rétablir l'affirmation juste
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - contexte_de_la_situation : quatre valeurs, et quatre seulement — prix, recettes,
 *     distances, échelles. Chaque situation porte son contexte ; toute carte à plusieurs
 *     situations balaie les quatre (point de vigilance 4).
 *   - nature_de_la_situation : proportionnelle, affine, paliers, non linéaire, sans relation.
 *   - nombre_de_situations_par_exercice : [3, 6].
 *   - proportion_de_situations_proportionnelles : [0,4 ; 0,6] sur l'exercice. Les QCM à une
 *     seule bonne réponse tirent à parité la question « laquelle relève » (une proportionnelle
 *     sur quatre) et « laquelle ne relève pas » (trois sur quatre) : la moyenne de série vaut
 *     0,5 et aucune réponse automatique ne paie.
 *   - nombre_de_personnes_de_la_recette : 2, 4, 6, 8 (les valeurs de l'automatisme).
 *   - masse_ou_quantite_du_contexte_prix : 1, 3 et 4,3 kg (les valeurs de l'automatisme) ; le
 *     cas décimal est servi à chaque série de prix (point de vigilance 8).
 *   - montant_fixe_d_une_situation_affine : [2, 30], et toujours inférieur à la part variable
 *     de la plus petite valeur affichée (point de vigilance 6).
 *   - nombre_de_couples_donnes_par_situation : 3 (trois couples, pour qu'une situation affine
 *     ne se confonde pas par coïncidence avec une proportionnelle).
 *   - prix_unitaire : [0,50 ; 40], à une décimale, pour que le prix de 4,3 kg tombe au centime
 *     (point de vigilance 7 : le quotient reste exactement constant, arrondi d'affichage
 *     compris).
 * Exclusions respectées : aucun agrandissement ni réduction de figure, aucune consigne qui
 *   affirme la proportionnalité avant la question, jamais toutes les situations du même côté,
 *   aucune situation ambiguë (ni consommation à la vitesse, ni temps de cuisson à la masse),
 *   aucune valeur nulle ou négative, aucun calcul demandé avant la décision.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. confondre écart constant et rapport constant  → `ecart_constant_pris_pour_rapport`
 *   2. juger proportionnelle une situation affine     → `affine_prise_pour_proportionnelle`
 *   3. juger non proportionnelle une situation qui l'est, parce que les nombres sont décimaux
 *      ou que le coefficient est inférieur à 1        → `decimaux_ou_coefficient_petit`
 *   4. juger proportionnelle une situation par paliers → `paliers_pris_pour_proportionnel`
 *   5. croire que « quand l'une augmente, l'autre augmente » suffit
 *                                                      → `croissance_commune`
 *   6. ne pas reconnaître qu'un doublement puis un triplement sont une multiplication par un
 *      même coefficient global                         → `passages_successifs_non_reconnus`
 *
 * Graduation → cartes :
 *   niveau 1 → decider_tableau_vf, decider_qcm_unique, decider_texte
 *   niveau 2 → raison_qcm_unique, raison_relier, raison_texte_trou_drag
 *   niveau 3 → valeurs_tableau_vf, valeurs_qcm_unique, valeurs_texte
 *   niveau 4 → voisines_qcm_unique, voisines_tableau_vf, voisines_texte_trou_drag
 *
 * Points non tranchés (voir audit/5eme/983.md) :
 *   - La justification en CHAMP LIBRE non corrigée automatiquement (type_reponse, graduation
 *     niveaux 3 et 4) n'existe pas dans le système : toute saisie est corrigée. Les niveaux 3
 *     et 4 recueillent donc la justification sous une forme corrigeable (raison à relier,
 *     affirmation à rétablir), et la séparation du barème décision/justification, qui suppose
 *     deux notes, n'est pas représentable dans un payload.
 *   - `graduation` niveau 4 cite « une recette et un temps de cuisson » alors que
 *     `exclusions` 4 écarte le temps de cuisson comme situation ambiguë. L'exclusion l'emporte :
 *     les couples de situations voisines sont un tarif proportionnel et un tarif affine.
 *
 * Invariants : les situations viennent d'une liste fermée et relue, chacune portant son
 * contexte, sa nature et sa raison ; arithmétique entière (centimes, grammes, dixièmes de
 * kilogramme) pour que les quotients affichés soient exactement égaux ; boucles bornées avec
 * repli déterministe, jamais de throw.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 983;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const COLONNES = ['Proportionnelle', 'Non proportionnelle'];

// ─── Mots-clés des erreurs visées (spec 983.json) ───
export const ERREURS_VISEES = {
    ecart_constant_pris_pour_rapport: 'Confondre écart constant et rapport constant : juger proportionnelle une situation où l\'on ajoute toujours la même chose',
    affine_prise_pour_proportionnelle: 'Juger proportionnelle une situation affine (frais de livraison, abonnement) parce que « plus on en prend, plus on paie »',
    decimaux_ou_coefficient_petit: 'Juger non proportionnelle une situation qui l\'est, parce que les nombres sont décimaux ou que le coefficient est inférieur à 1',
    paliers_pris_pour_proportionnel: 'Juger proportionnelle une situation par paliers, parce que les premières valeurs se comportent comme si elle l\'était',
    croissance_commune: 'Croire que « quand l\'une augmente, l\'autre augmente » suffit à établir la proportionnalité',
    passages_successifs_non_reconnus: 'Juger non proportionnelle une recette doublée puis retriplée, faute de voir que ces passages sont une multiplication par un même coefficient global',
};

export const REGLES_DISTRACTEURS_QCM = { ...ERREURS_VISEES };

// ═══════════════════════════════════════════════════════════
// HELPERS D'AFFICHAGE (arithmétique entière, écriture française)
// ═══════════════════════════════════════════════════════════

/** Centimes → montant français (« 903 » → « 9,03 », « 600 » → « 6 »). */
const centimes = (c) => {
    const e = Math.floor(c / 100);
    const r = c % 100;
    return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0')}`;
};

/** Centimes → montant à deux décimales imposées (« 600 » → « 6,00 »). */
const centimes2 = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;

/** Dixièmes → écriture française (« 43 » → « 4,3 », « 30 » → « 3 »). */
const dixiemes = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

// ═══════════════════════════════════════════════════════════
// LISTE FERMÉE DES SITUATIONS (relue : chacune a une réponse
// que l'enseignant défend sans hésiter — point de vigilance 3)
// ═══════════════════════════════════════════════════════════

/**
 * Chaque situation porte : son contexte (l'un des quatre du programme), sa nature,
 * la décision attendue et la raison juste. Les raisons fautives sont communes et
 * codées par l'erreur visée qu'elles servent.
 */
const SITUATIONS = [
    // ── Contexte « prix » ──
    {
        id: 'prix_tomates', contexte: 'prix', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Chez le primeur, les tomates sont vendues 2,10 € le kilogramme. On observe la masse achetée et le prix payé.',
        raison: 'Oui, parce que le prix s\'obtient en multipliant toujours la masse par le prix d\'un kilogramme.',
    },
    {
        id: 'prix_tissu', contexte: 'prix', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Un marchand vend du tissu 0,90 € le décimètre. On observe la longueur coupée et le prix payé.',
        raison: 'Oui, parce que le prix s\'obtient en multipliant toujours la longueur par le prix d\'un décimètre.',
    },
    {
        id: 'prix_livraison', contexte: 'prix', nature: 'affine', proportionnelle: false,
        phrase: 'Un site de vente facture 4 € de frais de livraison pour toute commande, puis 3 € par article. On observe le nombre d\'articles et le prix total.',
        raison: 'Non, parce qu\'un montant fixe de 4 € s\'ajoute, quel que soit le nombre d\'articles.',
    },
    {
        id: 'prix_piscine', contexte: 'prix', nature: 'paliers', proportionnelle: false,
        phrase: 'À la piscine, l\'entrée coûte 4 € à l\'unité, mais 3 € l\'entrée à partir de dix entrées achetées ensemble. On observe le nombre d\'entrées et le prix payé.',
        raison: 'Non, parce que le prix d\'une entrée change à partir de dix entrées.',
    },
    // ── Contexte « recettes » ──
    {
        id: 'recette_farine', contexte: 'recettes', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Une recette de crêpes pour 4 personnes demande 250 g de farine. On observe le nombre de personnes et la masse de farine.',
        raison: 'Oui, parce que la masse de farine s\'obtient en multipliant toujours le nombre de personnes par la même quantité.',
    },
    {
        id: 'recette_sucre', contexte: 'recettes', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Une recette de compote pour 4 personnes demande 60 g de sucre. On observe le nombre de personnes et la masse de sucre.',
        raison: 'Oui, parce que la masse de sucre s\'obtient en multipliant toujours le nombre de personnes par la même quantité.',
    },
    {
        id: 'recette_page', contexte: 'recettes', nature: 'sans_relation', proportionnelle: false,
        phrase: 'Dans un livre de cuisine, on observe le numéro de la page d\'une recette et le nombre de personnes pour lequel elle est écrite.',
        raison: 'Non, parce qu\'un numéro de page et un nombre de personnes n\'ont aucun lien entre eux.',
    },
    {
        id: 'recette_moules', contexte: 'recettes', nature: 'paliers', proportionnelle: false,
        phrase: 'Un moule à gâteau sert jusqu\'à 6 parts ; au-delà, il en faut un deuxième, puis un troisième au-delà de 12 parts. On observe le nombre de parts et le nombre de moules.',
        raison: 'Non, parce que le nombre de moules change par paliers et non par multiplication.',
    },
    // ── Contexte « distances » ──
    {
        id: 'distance_train', contexte: 'distances', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Un train roule à allure constante sans jamais s\'arrêter. On observe la durée du trajet et la distance parcourue.',
        raison: 'Oui, parce que la distance s\'obtient en multipliant toujours la durée par la même distance parcourue en une heure.',
    },
    {
        id: 'distance_robot', contexte: 'distances', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Un robot avance de 0,8 mètre par seconde et ne change jamais d\'allure. On observe la durée et la distance parcourue.',
        raison: 'Oui, parce que la distance s\'obtient en multipliant toujours la durée par 0,8 mètre.',
    },
    {
        id: 'distance_pause', contexte: 'distances', nature: 'sans_relation', proportionnelle: false,
        phrase: 'Un cycliste roule une heure, s\'arrête une demi-heure pour déjeuner, puis repart. On observe la durée écoulée depuis le départ et la distance parcourue.',
        raison: 'Non, parce que pendant la pause la durée augmente sans que la distance change.',
    },
    {
        id: 'distance_marcheur', contexte: 'distances', nature: 'sans_relation', proportionnelle: false,
        phrase: 'Un marcheur parcourt 4 km la première heure, 3 km la deuxième et 2 km la troisième. On observe la durée écoulée et la distance totale parcourue.',
        raison: 'Non, parce que la distance parcourue en une heure n\'est pas toujours la même.',
    },
    // ── Contexte « échelles » ──
    {
        id: 'echelle_carte', contexte: 'echelles', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Sur une carte à l\'échelle 1 pour 25 000, on observe une distance mesurée sur la carte et la distance réelle correspondante.',
        raison: 'Oui, parce que la distance réelle s\'obtient en multipliant toujours la distance mesurée par 25 000.',
    },
    {
        id: 'echelle_plan', contexte: 'echelles', nature: 'proportionnelle', proportionnelle: true,
        phrase: 'Sur le plan d\'un appartement, 1 cm du plan représente 50 cm en réalité. On observe une longueur du plan et la longueur réelle correspondante.',
        raison: 'Oui, parce que la longueur réelle s\'obtient en multipliant toujours la longueur du plan par 50.',
    },
    {
        id: 'echelle_aire', contexte: 'echelles', nature: 'non_lineaire', proportionnelle: false,
        phrase: 'Sur le plan d\'un appartement, on observe le côté d\'une pièce carrée mesuré sur le plan et l\'aire réelle de cette pièce.',
        raison: 'Non, parce que si le côté double, l\'aire est multipliée par quatre.',
    },
    {
        id: 'echelle_atlas', contexte: 'echelles', nature: 'sans_relation', proportionnelle: false,
        phrase: 'Dans un atlas, on observe le numéro de la page et la distance réelle entre les deux villes qui y figurent.',
        raison: 'Non, parce qu\'un numéro de page et une distance réelle n\'ont aucun lien entre eux.',
    },
];

const CONTEXTES = ['prix', 'recettes', 'distances', 'echelles'];

/** Raisons fautives, chacune adossée à une erreur visée. */
const RAISONS_FAUSSES = [
    { code: 'croissance_commune', decision: true, texte: 'Oui, parce que quand l\'une des deux grandeurs augmente, l\'autre augmente aussi.' },
    { code: 'ecart_constant_pris_pour_rapport', decision: true, texte: 'Oui, parce qu\'on ajoute toujours la même chose d\'une valeur à la suivante.' },
    { code: 'affine_prise_pour_proportionnelle', decision: true, texte: 'Oui, parce que chaque unité de plus apporte toujours la même quantité en face.' },
    { code: 'paliers_pris_pour_proportionnel', decision: true, texte: 'Oui, parce que les premières valeurs se comportent comme des valeurs proportionnelles.' },
    { code: 'decimaux_ou_coefficient_petit', decision: false, texte: 'Non, parce que les nombres en jeu ne sont pas des entiers.' },
    { code: 'passages_successifs_non_reconnus', decision: false, texte: 'Non, parce qu\'on double d\'abord, puis on triple : ce n\'est pas la même opération.' },
];

/** Une situation par contexte, avec la répartition des décisions demandée. */
const tirerQuatreSituations = (nbProportionnelles) => {
    for (let essai = 0; essai < 80; essai++) {
        const contextes = shuffle([...CONTEXTES]);
        const vrais = shuffle(new Array(4).fill(false).map((_, i) => i < nbProportionnelles));
        const choisies = [];
        let ok = true;
        contextes.forEach((ctx, i) => {
            const pool = SITUATIONS.filter((s) => s.contexte === ctx && s.proportionnelle === vrais[i]);
            if (!pool.length) { ok = false; return; }
            choisies.push(randomChoice(pool));
        });
        if (ok && choisies.length === 4) return choisies;
    }
    return [
        SITUATIONS.find((s) => s.id === 'prix_tomates'),
        SITUATIONS.find((s) => s.id === 'recette_page'),
        SITUATIONS.find((s) => s.id === 'distance_train'),
        SITUATIONS.find((s) => s.id === 'echelle_aire'),
    ];
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 — DÉCIDER (niveau 1)
// ═══════════════════════════════════════════════════════════

/** L'erreur visée que sert une situation non proportionnelle donnée. */
const codeDeNature = (nature) => {
    if (nature === 'affine') return 'affine_prise_pour_proportionnelle';
    if (nature === 'paliers') return 'paliers_pris_pour_proportionnel';
    return 'croissance_commune';
};

const genDeciderVF = () => {
    const situations = shuffle(tirerQuatreSituations(2));
    const payload = makeVraiFaux(
        situations.map((s) => s.phrase),
        situations.map((s) => (s.proportionnelle ? 0 : 1)),
        '<p>Pour chaque situation, dis si les deux grandeurs observées sont proportionnelles.</p>',
        { ...opts('Décider pour quatre situations'), colonnes: COLONNES },
    );
    payload._meta.params = {
        niveau: 1,
        situations: situations.map((s) => ({ id: s.id, contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle })),
        distracteurs: situations.filter((s) => !s.proportionnelle).map((s) => ({ code: codeDeNature(s.nature) })),
    };
    return payload;
};

const genDeciderQcm = () => {
    // À parité : « laquelle relève » (1 proportionnelle sur 4) et « laquelle ne relève pas »
    // (3 sur 4). La moyenne des deux vaut 0,5 (variable proportion_de_situations…).
    const chercheProportionnelle = randInt(0, 1) === 0;
    const situations = shuffle(tirerQuatreSituations(chercheProportionnelle ? 1 : 3));
    const bonne = situations.find((s) => s.proportionnelle === chercheProportionnelle);
    const autres = situations.filter((s) => s !== bonne);
    const distracteurs = autres.map((s) => ({
        code: chercheProportionnelle ? codeDeNature(s.nature) : 'decimaux_ou_coefficient_petit',
        valeur: s.phrase,
    }));
    const consigne = chercheProportionnelle
        ? '<p>Une seule de ces situations met en jeu deux grandeurs proportionnelles. Laquelle ?</p>'
        : '<p>Une seule de ces situations ne met pas en jeu deux grandeurs proportionnelles. Laquelle ?</p>';
    const payload = makeQcm(situations.map((s) => s.phrase), bonne.phrase, consigne, opts('Repérer la situation à part'));
    payload._meta.params = {
        niveau: 1, sens: chercheProportionnelle ? 'relever' : 'exclure',
        situations: situations.map((s) => ({ id: s.id, contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle })),
        reponse: bonne.id, distracteurs,
    };
    return payload;
};

const genDeciderSaisie = () => {
    const chercheProportionnelle = randInt(0, 1) === 0;
    const situations = shuffle(tirerQuatreSituations(chercheProportionnelle ? 1 : 3));
    const index = situations.findIndex((s) => s.proportionnelle === chercheProportionnelle);
    const lignes = situations.map((s, i) => `<p>Situation ${i + 1}. ${s.phrase}</p>`).join('');
    const consigne = chercheProportionnelle
        ? 'Une seule de ces situations met en jeu deux grandeurs proportionnelles. Écris son numéro.'
        : 'Une seule de ces situations ne met pas en jeu deux grandeurs proportionnelles. Écris son numéro.';
    const payload = makeSaisie(
        String(index + 1),
        `<p>${consigne}</p>${lignes}`,
        { ...opts('Donner le numéro de la situation à part'), strict: true },
    );
    payload._meta.params = {
        niveau: 1, sens: chercheProportionnelle ? 'relever' : 'exclure',
        situations: situations.map((s) => ({ id: s.id, contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle })),
        reponse: String(index + 1), erreurVisee: chercheProportionnelle ? 'croissance_commune' : 'decimaux_ou_coefficient_petit',
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 — DÉCIDER ET DIRE POURQUOI (niveau 2)
// ═══════════════════════════════════════════════════════════

const genRaisonQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const s = randomChoice(SITUATIONS);
        // Trois raisons fautives, dont au moins une qui donnerait la bonne DÉCISION :
        // une décision juste appuyée sur une raison fausse ne doit pas payer.
        const memeDecision = RAISONS_FAUSSES.filter((r) => r.decision === s.proportionnelle);
        const autreDecision = RAISONS_FAUSSES.filter((r) => r.decision !== s.proportionnelle);
        if (memeDecision.length < 2 || autreDecision.length < 1) continue;
        const choisies = [...shuffle([...memeDecision]).slice(0, 2), ...shuffle([...autreDecision]).slice(0, 1)];
        const valeurs = [s.raison, ...choisies.map((r) => r.texte)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(
            valeurs, s.raison,
            `<p>${s.phrase}</p><p>Ces deux grandeurs sont-elles proportionnelles, et pour quelle raison ?</p>`,
            opts('Décider et choisir la raison'),
        );
        payload._meta.params = {
            niveau: 2, situation: s.id, contexte: s.contexte, nature: s.nature,
            proportionnelle: s.proportionnelle, reponse: s.raison,
            distracteurs: choisies.map((r) => ({ code: r.code, valeur: r.texte })),
        };
        return payload;
    }
    const s = SITUATIONS[0];
    const choisies = [RAISONS_FAUSSES[0], RAISONS_FAUSSES[1], RAISONS_FAUSSES[4]];
    const payload = makeQcm(
        [s.raison, ...choisies.map((r) => r.texte)], s.raison,
        `<p>${s.phrase}</p><p>Ces deux grandeurs sont-elles proportionnelles, et pour quelle raison ?</p>`,
        opts('Décider et choisir la raison'),
    );
    payload._meta.params = {
        niveau: 2, situation: s.id, contexte: s.contexte, nature: s.nature,
        proportionnelle: s.proportionnelle, reponse: s.raison,
        distracteurs: choisies.map((r) => ({ code: r.code, valeur: r.texte })),
    };
    return payload;
};

const genRaisonRelier = () => {
    let situations = shuffle(tirerQuatreSituations(2));
    // Deux situations ne doivent jamais partager la même raison : la cible serait
    // dupliquée et l'un des deux appariements justes compté faux.
    for (let essai = 0; essai < 40 && new Set(situations.map((s) => s.raison)).size !== 4; essai++) {
        situations = shuffle(tirerQuatreSituations(2));
    }
    const pairs = situations.map((s) => ({ source: s.phrase, target: s.raison }));
    const payload = makeRelier(
        pairs,
        '<p>Relie chaque situation à la raison qui dit si ses deux grandeurs sont proportionnelles.</p>',
        opts('Relier une situation et sa raison'),
    );
    payload._meta.params = {
        niveau: 2,
        situations: situations.map((s) => ({ id: s.id, contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle })),
        erreursVisees: ['croissance_commune'],
    };
    return payload;
};

/**
 * Justifications à trous. Deux formulations par nature (variété des tirages) ;
 * dans chaque phrase, les deux mots à placer sont distincts et les distracteurs
 * ne sont jamais égaux à un mot de trou.
 */
const TROUS_PAR_NATURE = {
    proportionnelle: [
        { phrase: 'Pour passer d\'une grandeur à l\'autre, on {{multiplie}} toujours par le même nombre\nLes deux grandeurs sont donc {{proportionnelles}}', distracteurs: ['ajoute', 'différentes'] },
        { phrase: 'Le quotient d\'une grandeur par l\'autre garde toujours la même {{valeur}}\nLes deux grandeurs sont donc {{proportionnelles}}', distracteurs: ['différence', 'indépendantes'] },
    ],
    affine: [
        { phrase: 'Une somme {{fixe}} s\'ajoute à chaque fois, quelle que soit la quantité\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['variable', 'proportionnelles'] },
        { phrase: 'On n\'obtient pas la seconde grandeur en {{multipliant}} la première par un seul nombre\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['ajoutant', 'proportionnelles'] },
    ],
    paliers: [
        { phrase: 'Le tarif change à partir d\'une certaine {{quantité}}\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['multiplication', 'proportionnelles'] },
        { phrase: 'Les premières valeurs se comportent comme si elles étaient proportionnelles, puis le prix d\'une unité {{change}}\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['reste le même', 'proportionnelles'] },
    ],
    non_lineaire: [
        { phrase: 'Quand la première grandeur double, la seconde est multipliée par {{quatre}}\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['deux', 'proportionnelles'] },
        { phrase: 'Le quotient d\'une grandeur par l\'autre {{change}} d\'un couple au suivant\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['reste le même', 'proportionnelles'] },
    ],
    sans_relation: [
        { phrase: 'Les deux grandeurs n\'ont aucun {{lien}} entre elles\nElles ne sont donc {{pas proportionnelles}}', distracteurs: ['rapport constant', 'proportionnelles'] },
        { phrase: 'L\'une peut augmenter pendant que l\'autre ne {{change pas}}\nLes deux grandeurs ne sont donc {{pas proportionnelles}}', distracteurs: ['double', 'proportionnelles'] },
    ],
};

const genRaisonTrou = () => {
    const s = randomChoice(SITUATIONS);
    const modele = randomChoice(TROUS_PAR_NATURE[s.nature]);
    const payload = makeTexteTrou(
        modele.phrase,
        `<p>${s.phrase}</p><p>Complète la justification avec les bons mots.</p>`,
        { ...opts('Compléter la justification'), mode: 'drag', distracteurs: modele.distracteurs },
    );
    payload._meta.params = {
        niveau: 2, situation: s.id, contexte: s.contexte, nature: s.nature,
        proportionnelle: s.proportionnelle,
        distracteurs: [{ code: s.proportionnelle ? 'ecart_constant_pris_pour_rapport' : 'croissance_commune', valeur: modele.distracteurs[0] }],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 3 — SÉRIES DE VALEURS (niveau 3)
// ═══════════════════════════════════════════════════════════

/**
 * Séries de valeurs du niveau 3. Prix au kilogramme à UNE décimale : le prix de
 * 4,3 kg tombe alors exactement au centime (point de vigilance 7), et les trois
 * masses de l'automatisme (1 kg, 3 kg, 4,3 kg) sont servies à chaque tirage
 * (point de vigilance 8).
 */
const DENREES = [
    { nom: 'de fruits', prix: [80, 120, 150, 210, 250, 340, 400, 750] },
    { nom: 'de viande', prix: [1200, 1800, 2500, 3200, 4000] },
    { nom: 'de fromage', prix: [900, 1100, 1400, 1600, 2000] },
];
const MASSES_DIXIEMES = [10, 30, 43];               // 1 kg, 3 kg, 4,3 kg (automatisme)
const PERSONNES = [2, 4, 6];                        // automatisme (2, 4, 6, 8)
const DUREES_H = [1, 2, 3];
const LONGUEURS_PLAN_CM = [2, 5, 7];
const INGREDIENTS = [
    { nom: 'de farine', parPersonne: [25, 30, 40, 45, 60, 75] },
    { nom: 'de sucre', parPersonne: [15, 20, 25, 30] },
    { nom: 'de beurre', parPersonne: [20, 25, 35, 50] },
];

/** Série proportionnelle du contexte donné (trois couples, quotient exactement constant). */
const serieProportionnelle = (contexte) => {
    if (contexte === 'prix') {
        const d = randomChoice(DENREES);
        const k = randomChoice(d.prix);
        return {
            contexte, nature: 'proportionnelle', proportionnelle: true,
            titre: `la masse ${d.nom} achetée et le prix payé`,
            couples: MASSES_DIXIEMES.map((m) => `${dixiemes(m)} kg pour ${centimes2((k * m) / 10)} €`),
        };
    }
    if (contexte === 'recettes') {
        const ing = randomChoice(INGREDIENTS);
        const parPersonne = randomChoice(ing.parPersonne);
        return {
            contexte, nature: 'proportionnelle', proportionnelle: true,
            titre: `le nombre de personnes et la masse ${ing.nom}`,
            couples: PERSONNES.map((p) => `${p} personnes pour ${p * parPersonne} g`),
        };
    }
    if (contexte === 'distances') {
        const parHeure = randomChoice([60, 70, 80, 90, 110]);
        return {
            contexte, nature: 'proportionnelle', proportionnelle: true,
            titre: 'la durée du trajet et la distance parcourue',
            couples: DUREES_H.map((h) => `${h} h pour ${h * parHeure} km`),
        };
    }
    const parCm = randomChoice([50, 100, 250, 500]);      // centimètres réels pour 1 cm de plan
    return {
        contexte, nature: 'proportionnelle', proportionnelle: true,
        titre: 'la longueur mesurée sur le plan et la longueur réelle',
        couples: LONGUEURS_PLAN_CM.map((c) => `${c} cm pour ${centimes(c * parCm)} m`),
    };
};

/** Série affine : un montant fixe s'ajoute, détectable mais jamais dominant. */
const serieAffine = (contexte) => {
    if (contexte === 'prix') {
        const k = randomChoice([120, 150, 210, 250, 340]);
        const fixe = randomChoice([200, 250, 300, 400]);     // 2 € à 4 €, sous la part variable
        return {
            contexte, nature: 'affine', proportionnelle: false, fixe,
            titre: 'le nombre d\'articles commandés et le prix total, livraison comprise',
            couples: [2, 3, 5].map((n) => `${n} articles pour ${centimes2(fixe + k * n)} €`),
        };
    }
    if (contexte === 'recettes') {
        const parPersonne = randomChoice([25, 30, 40]);
        const fixe = randomChoice([20, 25, 30]);
        return {
            contexte, nature: 'affine', proportionnelle: false, fixe,
            titre: 'le nombre de personnes et la masse de pâte à prévoir',
            couples: PERSONNES.map((p) => `${p} personnes pour ${fixe + p * parPersonne} g`),
        };
    }
    if (contexte === 'distances') {
        const parHeure = randomChoice([60, 70, 80]);
        const depart = randomChoice([5, 10, 15]);
        return {
            contexte, nature: 'affine', proportionnelle: false, fixe: depart,
            titre: 'la durée depuis le péage et la distance depuis le domicile',
            couples: DUREES_H.map((h) => `${h} h pour ${depart + h * parHeure} km`),
        };
    }
    const parCm = randomChoice([100, 250]);
    const fixe = randomChoice([2, 3]);
    return {
        contexte, nature: 'affine', proportionnelle: false, fixe,
        titre: 'la longueur mesurée sur le croquis et la longueur réelle',
        couples: LONGUEURS_PLAN_CM.map((c) => `${c} cm pour ${centimes(fixe * 100 + c * parCm)} m`),
    };
};

/** Série à écart constant des deux côtés, mais de quotients différents (erreur visée 1). */
const serieEcartConstant = (contexte) => {
    const depart = randInt(3, 7);
    const pas = randInt(2, 5);
    const valeurs = [0, 1, 2].map((i) => ({ x: 2 + 2 * i, y: depart + pas * i }));
    if (contexte === 'recettes') {
        return {
            contexte, nature: 'ecart_constant', proportionnelle: false,
            titre: 'le nombre de parts et le nombre de cuillères de sucre',
            couples: valeurs.map((v) => `${v.x} parts pour ${v.y} cuillères`),
        };
    }
    if (contexte === 'distances') {
        return {
            contexte, nature: 'ecart_constant', proportionnelle: false,
            titre: 'le numéro de l\'étape et la distance parcourue ce jour-là',
            couples: valeurs.map((v) => `étape ${v.x} pour ${v.y} km`),
        };
    }
    if (contexte === 'echelles') {
        return {
            contexte, nature: 'ecart_constant', proportionnelle: false,
            titre: 'la longueur sur le croquis et la longueur réelle',
            couples: valeurs.map((v) => `${v.x} cm pour ${v.y} m`),
        };
    }
    return {
        contexte: 'prix', nature: 'ecart_constant', proportionnelle: false,
        titre: 'le numéro de la rangée et le prix de la place',
        couples: valeurs.map((v) => `rangée ${v.x} pour ${v.y} €`),
    };
};

/** Série par paliers (erreur visée 4). Deux contextes seulement la portent. */
const seriePaliers = (contexte) => {
    if (contexte === 'recettes') {
        return {
            contexte, nature: 'paliers', proportionnelle: false,
            titre: 'le nombre de parts et le nombre de moules nécessaires',
            couples: ['4 parts pour 1 moule', '6 parts pour 1 moule', '12 parts pour 2 moules'],
        };
    }
    const plein = randomChoice([4, 5, 6]);
    const reduit = plein - 1;
    return {
        contexte: 'prix', nature: 'paliers', proportionnelle: false,
        titre: 'le nombre d\'entrées achetées et le prix payé',
        couples: [
            `2 entrées pour ${2 * plein} €`,
            `5 entrées pour ${5 * plein} €`,
            `10 entrées pour ${10 * reduit} €`,
        ],
    };
};

/** Constructeurs de séries NON proportionnelles admissibles par contexte. */
const NON_PROP_PAR_CONTEXTE = {
    prix: [serieAffine, serieEcartConstant, seriePaliers],
    recettes: [serieAffine, serieEcartConstant, seriePaliers],
    distances: [serieAffine, serieEcartConstant],
    echelles: [serieAffine, serieEcartConstant],
};

const codeDeSerie = (serie) => {
    if (serie.nature === 'paliers') return 'paliers_pris_pour_proportionnel';
    if (serie.nature === 'affine') return 'affine_prise_pour_proportionnelle';
    return 'ecart_constant_pris_pour_rapport';
};

/** n séries, dont nbProp proportionnelles, chacune sur un contexte différent. */
const tirerSeries = (n, nbProp) => {
    const contextes = shuffle([...CONTEXTES]).slice(0, n);
    const plan = shuffle(new Array(n).fill(false).map((_, i) => i < nbProp));
    return contextes.map((ctx, i) => (plan[i]
        ? serieProportionnelle(ctx)
        : randomChoice(NON_PROP_PAR_CONTEXTE[ctx])(ctx)));
};

/**
 * Le numéro n'est écrit que là où l'élève doit le rendre (saisie) ou le lire en ligne
 * (tableau) : dans un QCM, les propositions sont mélangées à l'affichage et un numéro
 * y contredirait l'ordre vu par l'élève.
 */
const texteSerie = (serie, i, avecNumero = true) => (avecNumero
    ? `Série ${i + 1} (${serie.titre}) ${serie.couples.join(' ; ')}`
    : `${cap(serie.titre)} : ${serie.couples.join(' ; ')}`);

const genValeursVF = () => {
    const series = tirerSeries(4, 2);
    const lignes = series.map((s, i) => texteSerie(s, i));
    const payload = makeVraiFaux(
        lignes,
        series.map((s) => (s.proportionnelle ? 0 : 1)),
        '<p>Pour chaque série de valeurs, dis si les deux grandeurs sont proportionnelles.</p>',
        { ...opts('Décider à partir de séries de valeurs'), colonnes: COLONNES },
    );
    payload._meta.params = {
        niveau: 3,
        series: series.map((s) => ({ contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle, fixe: s.fixe, couples: s.couples })),
        distracteurs: series.filter((s) => !s.proportionnelle).map((s) => ({ code: codeDeSerie(s) })),
    };
    return payload;
};

const genValeursQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const chercheProportionnelle = randInt(0, 1) === 0;
        const series = tirerSeries(4, chercheProportionnelle ? 1 : 3);
        const textes = series.map((s, i) => texteSerie(s, i, false));
        if (new Set(textes).size !== 4) continue;
        const index = series.findIndex((s) => s.proportionnelle === chercheProportionnelle);
        if (index < 0) continue;
        const distracteurs = series
            .map((s, i) => ({ s, i }))
            .filter(({ i }) => i !== index)
            .map(({ s, i }) => ({
                code: chercheProportionnelle ? codeDeSerie(s) : 'decimaux_ou_coefficient_petit',
                valeur: textes[i],
            }));
        const consigne = chercheProportionnelle
            ? '<p>Une seule de ces séries met en jeu deux grandeurs proportionnelles. Laquelle ?</p>'
            : '<p>Une seule de ces séries ne met pas en jeu deux grandeurs proportionnelles. Laquelle ?</p>';
        const payload = makeQcm(textes, textes[index], consigne, opts('Repérer la série à part'));
        payload._meta.params = {
            niveau: 3, sens: chercheProportionnelle ? 'relever' : 'exclure',
            series: series.map((s) => ({ contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle, fixe: s.fixe, couples: s.couples })),
            distracteurs,
        };
        return payload;
    }
    const series = tirerSeries(4, 1);
    const textes = series.map((s, i) => texteSerie(s, i, false));
    const index = series.findIndex((s) => s.proportionnelle);
    const payload = makeQcm(textes, textes[index],
        '<p>Une seule de ces séries met en jeu deux grandeurs proportionnelles. Laquelle ?</p>',
        opts('Repérer la série à part'));
    payload._meta.params = {
        niveau: 3, sens: 'relever',
        series: series.map((s) => ({ contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle, fixe: s.fixe, couples: s.couples })),
        distracteurs: series.map((s, i) => ({ s, i })).filter(({ s: x }) => !x.proportionnelle).map(({ s: x, i }) => ({ code: codeDeSerie(x), valeur: textes[i] })),
    };
    return payload;
};

const genValeursSaisie = () => {
    const chercheProportionnelle = randInt(0, 1) === 0;
    const series = tirerSeries(4, chercheProportionnelle ? 1 : 3);
    const index = series.findIndex((s) => s.proportionnelle === chercheProportionnelle);
    const lignes = series.map((s, i) => `<p>${texteSerie(s, i)}</p>`).join('');
    const consigne = chercheProportionnelle
        ? 'Une seule de ces séries met en jeu deux grandeurs proportionnelles. Écris son numéro.'
        : 'Une seule de ces séries ne met pas en jeu deux grandeurs proportionnelles. Écris son numéro.';
    const payload = makeSaisie(
        String(index + 1),
        `<p>${consigne}</p>${lignes}`,
        { ...opts('Donner le numéro de la série à part'), strict: true },
    );
    payload._meta.params = {
        niveau: 3, sens: chercheProportionnelle ? 'relever' : 'exclure',
        series: series.map((s) => ({ contexte: s.contexte, nature: s.nature, proportionnelle: s.proportionnelle, fixe: s.fixe, couples: s.couples })),
        reponse: String(index + 1), erreurVisee: 'ecart_constant_pris_pour_rapport',
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 4 — SITUATIONS VOISINES (niveau 4)
// ═══════════════════════════════════════════════════════════

const COMMERCES = [
    { nom: 'la boulangerie du marché', unite: 'brioche', unites: 'brioches' },
    { nom: 'le kiosque du parc', unite: 'barquette', unites: 'barquettes' },
    { nom: 'la papeterie centrale', unite: 'cahier', unites: 'cahiers' },
    { nom: 'la jardinerie des Lilas', unite: 'sachet de graines', unites: 'sachets de graines' },
    { nom: 'le magasin de sport', unite: 'balle de tennis', unites: 'balles de tennis' },
];

/** Majuscule initiale pour un nom placé en tête de phrase. */
const cap = (mot) => mot.charAt(0).toUpperCase() + mot.slice(1);
/** « à » contracté (« au kiosque », « à la boulangerie »). */
const aLieu = (nom) => (nom.startsWith('le ') ? `au ${nom.slice(3)}` : `à ${nom}`);
/** « de » contracté (« du kiosque », « de la boulangerie »). */
const duLieu = (nom) => (nom.startsWith('le ') ? `du ${nom.slice(3)}` : `de ${nom}`);

/**
 * Deux tarifs très proches : l'un proportionnel, l'autre affine à petit montant fixe
 * (graduation niveau 4). Le montant fixe reste détectable mais ne domine pas.
 */
const tirerVoisines = () => {
    const [a, b] = shuffle([...COMMERCES]).slice(0, 2);
    const k = randomChoice([120, 150, 200, 250, 300]);
    const fixe = randomChoice([200, 250, 300]);
    const quantites = [2, 4, 6];
    return {
        proportionnel: { ...a, couples: quantites.map((n) => `${n} ${a.unites} pour ${centimes2(k * n)} €`) },
        affine: { ...b, couples: quantites.map((n) => `${n} ${b.unites} pour ${centimes2(fixe + k * n)} €`) },
        k, fixe, quantites,
    };
};

const enonceVoisines = (v) => `<p>${cap(v.proportionnel.nom)} affiche ${v.proportionnel.couples.join(' ; ')}.</p>`
    + `<p>${cap(v.affine.nom)} affiche ${v.affine.couples.join(' ; ')}.</p>`;

const genVoisinesQcm = () => {
    const v = tirerVoisines();
    const bonne = `C'est faux, seul le tarif ${duLieu(v.proportionnel.nom)} est proportionnel, car ${aLieu(v.affine.nom)} un montant fixe de ${centimes(v.fixe)} € s'ajoute à chaque commande.`;
    const distracteurs = [
        { code: 'croissance_commune', valeur: 'C\'est vrai, dans les deux tarifs le prix augmente quand la quantité augmente, donc les deux sont proportionnels.' },
        { code: 'affine_prise_pour_proportionnelle', valeur: `C'est vrai, dans les deux tarifs chaque ${v.affine.unite} de plus coûte la même chose, donc les deux sont proportionnels.` },
        { code: 'ecart_constant_pris_pour_rapport', valeur: `C'est faux, seul le tarif ${duLieu(v.affine.nom)} est proportionnel, car le prix y augmente toujours du même écart.` },
    ];
    const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
    const payload = makeQcm(
        valeurs, bonne,
        enonceVoisines(v)
        + '<p>Un élève affirme : « Les deux tarifs sont proportionnels. » Quelle réponse corrige exactement cette affirmation ?</p>',
        opts('Corriger une affirmation sur deux tarifs voisins'),
    );
    payload._meta.params = {
        niveau: 4, fixe: v.fixe, coefficient: v.k,
        contextes: ['prix'], reponse: bonne, distracteurs,
    };
    return payload;
};

const genVoisinesVF = () => {
    const v = tirerVoisines();
    const plan = shuffle([true, true, false, false]);
    const lignes = [
        plan[0]
            ? `${cap(aLieu(v.proportionnel.nom))}, le prix payé est proportionnel au nombre de ${v.proportionnel.unites}`
            : `${cap(aLieu(v.proportionnel.nom))}, le prix payé n'est pas proportionnel au nombre de ${v.proportionnel.unites}`,
        plan[1]
            ? `${cap(aLieu(v.affine.nom))}, le prix payé n'est pas proportionnel au nombre de ${v.affine.unites}`
            : `${cap(aLieu(v.affine.nom))}, le prix payé est proportionnel au nombre de ${v.affine.unites}`,
        plan[2]
            ? `${cap(aLieu(v.affine.nom))}, chaque ${v.affine.unite} de plus coûte ${centimes(v.k)} €`
            : `${cap(aLieu(v.affine.nom))}, chaque ${v.affine.unite} de plus coûte ${centimes(v.k + 100)} €`,
        plan[3]
            ? 'Chez les deux commerçants, le prix augmente quand la quantité augmente'
            : 'Chez les deux commerçants, le prix reste le même quelle que soit la quantité',
    ];
    const payload = makeVraiFaux(
        lignes, plan.map((x) => (x ? 0 : 1)),
        enonceVoisines(v)
        + '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux : deux tarifs voisins'),
    );
    payload._meta.params = {
        niveau: 4, fixe: v.fixe, coefficient: v.k, contextes: ['prix'],
        distracteurs: [
            { code: 'affine_prise_pour_proportionnelle', valeur: v.affine.nom },
            { code: 'croissance_commune', valeur: 'le prix augmente quand la quantité augmente' },
        ],
    };
    return payload;
};

const genVoisinesTrou = () => {
    const v = tirerVoisines();
    const phrase = `L'affirmation est {{fausse}}\n`
        + `${cap(aLieu(v.affine.nom))}, une somme de ${centimes(v.fixe)} € s'ajoute à chaque commande, on ne passe donc pas d'une grandeur à l'autre par une seule {{multiplication}}\n`
        + `${cap(aLieu(v.proportionnel.nom))}, le prix et la quantité sont {{proportionnels}}`;
    const payload = makeTexteTrou(
        phrase,
        enonceVoisines(v)
        + '<p>Un élève affirme : « Les deux tarifs sont proportionnels. » Complète la réponse qui rétablit ce qui est juste.</p>',
        { ...opts('Rétablir l\'affirmation juste'), mode: 'drag', distracteurs: ['juste', 'addition', 'égaux'] },
    );
    payload._meta.params = {
        niveau: 4, fixe: v.fixe, coefficient: v.k, contextes: ['prix'],
        distracteurs: [{ code: 'affine_prise_pour_proportionnelle', valeur: 'juste' }, { code: 'ecart_constant_pris_pour_rapport', valeur: 'addition' }],
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    decider_tableau_vf: genDeciderVF,
    decider_qcm_unique: genDeciderQcm,
    decider_texte: genDeciderSaisie,

    raison_qcm_unique: genRaisonQcm,
    raison_relier: genRaisonRelier,
    raison_texte_trou_drag: genRaisonTrou,

    valeurs_tableau_vf: genValeursVF,
    valeurs_qcm_unique: genValeursQcm,
    valeurs_texte: genValeursSaisie,

    voisines_qcm_unique: genVoisinesQcm,
    voisines_tableau_vf: genVoisinesVF,
    voisines_texte_trou_drag: genVoisinesTrou,
};

export const CATEGORIES = [
    {
        id: 'decider',
        label: 'Décider : quatre situations, une par contexte',
        formatsList: [
            { key: 'decider_tableau_vf', label: 'Tableau' },
            { key: 'decider_qcm_unique', label: 'QCM' },
            { key: 'decider_texte', label: 'Saisie' },
        ],
    },
    {
        id: 'raison',
        label: 'Décider et dire pourquoi',
        formatsList: [
            { key: 'raison_qcm_unique', label: 'QCM' },
            { key: 'raison_relier', label: 'Relier' },
            { key: 'raison_texte_trou_drag', label: 'Trous' },
        ],
    },
    {
        id: 'valeurs',
        label: 'Décider à partir de séries de valeurs',
        formatsList: [
            { key: 'valeurs_tableau_vf', label: 'Tableau' },
            { key: 'valeurs_qcm_unique', label: 'QCM' },
            { key: 'valeurs_texte', label: 'Saisie' },
        ],
    },
    {
        id: 'voisines',
        label: 'Deux tarifs voisins : corriger l\'affirmation',
        formatsList: [
            { key: 'voisines_qcm_unique', label: 'QCM' },
            { key: 'voisines_tableau_vf', label: 'Tableau' },
            { key: 'voisines_texte_trou_drag', label: 'Trous' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
